/**
 * Проверка правил акции.
 *
 * У каждой запущенной акции есть хеш её правил, опубликованный при запуске и
 * неизменный после. Этот экран делает из хеша что-то полезное: он берёт текст
 * правил, считает SHA-256 прямо в браузере и сравнивает с опубликованным.
 *
 * Зачем это участнику: правила — единственное, что обещает ему организатор.
 * Хеш доказывает, что текст, который он читает сегодня, — тот же, что был в
 * день его участия. А поле «свой экземпляр» отвечает на обратный вопрос: если
 * человек сохранил правила себе, он может проверить, не разошлись ли они с
 * тем, что опубликовано сейчас.
 *
 * Считает браузер, а не сервер: проверка, которую делает проверяемый, ничего
 * не стоит.
 */
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, Field, Hash, Icon, Skeleton, fmtDateTime } from '@allswipes/ui';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../api';
import { Shell } from '../components/Shell';
import { useResource } from '../hooks';

import { useT } from '@allswipes/i18n';

/** SHA-256 текста в hex — ровно то же, что считает сервер над теми же байтами. */
async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function RulesScreen() {
  const t = useT();
  const { id = '' } = useParams();
  const view = useResource(() => api.campaign(id), [id]);

  const [computed, setComputed] = useState<string | null>(null);
  const [mine, setMine] = useState('');
  const [mineHash, setMineHash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rules = view.data?.campaign.rules_text ?? '';
  const published = view.data?.campaign.rules_hash ?? '';

  useEffect(() => {
    if (!rules) return;
    sha256Hex(rules)
      .then(setComputed)
      .catch(() => setError(t('Браузер не дал посчитать хеш. Открой страницу по https.')));
  }, [rules]);

  const matches = useMemo(
    () => computed !== null && published !== '' && computed === published.toLowerCase(),
    [computed, published],
  );

  if (view.loading) {
    return (
      <Shell>
        <Skeleton count={2} height={120} />
      </Shell>
    );
  }
  if (view.error || !view.data) {
    return (
      <Shell title={t('Правила')}>
        <Empty icon="search" title={t('Акция не найдена')}>
          {view.error ?? t('Возможно, ссылка устарела.')}
        </Empty>
      </Shell>
    );
  }

  const c = view.data.campaign;

  const checkMine = async () => {
    setError(null);
    try {
      setMineHash(await sha256Hex(mine));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Не получилось посчитать хеш'));
    }
  };

  return (
    <Shell title={t('Правила акции')} subtitle={c.name}>
      <Card style={{ marginBottom: 14 }}>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="h2">{t('Проверка текста')}</span>
          <span className="spacer" />
          {published === '' ? (
            <Badge tone="warn">{t('хеш не опубликован')}</Badge>
          ) : matches ? (
            <Badge tone="good">{t('совпадает')}</Badge>
          ) : computed === null ? (
            <Badge>{t('считаем…')}</Badge>
          ) : (
            <Badge tone="bad">{t('не совпадает')}</Badge>
          )}
        </div>

        <div className="stack">
          <div className="krow col">
            <span className="k">{t('Опубликовано при запуске')}</span>
            <span className="v mono">{published || '—'}</span>
          </div>
          <div className="krow col">
            <span className="k">{t('Посчитано сейчас в твоём браузере')}</span>
            <span className="v mono">{computed ?? '…'}</span>
          </div>
          {c.locked_at && (
            <div className="krow">
              <span className="k">{t('Условия зафиксированы')}</span>
              <span className="v">{fmtDateTime(c.locked_at)}</span>
            </div>
          )}
        </div>

        <p className="sub" style={{ marginTop: 12 }}>
          {matches
            ? t('Текст правил ниже — ровно тот, что был опубликован при запуске акции. Изменить его после запуска нельзя: это сломало бы хеш, который уже разошёлся по страницам и скриншотам.')
            : t('Хеш считается как SHA-256 от текста правил целиком, байт в байт. Расхождение означает, что текст отличается хотя бы одним символом — об этом стоит спросить организатора по контакту из правил.')}
        </p>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <div className="h2" style={{ marginBottom: 6 }}>
          {t('Свой экземпляр')}
        </div>
        <p className="sub" style={{ marginBottom: 10 }}>
          {t('Если ты сохранил правила себе, вставь их сюда — браузер посчитает хеш и сравнит с опубликованным. Текст никуда не отправляется.')}
        </p>
        <Field label={t('Текст правил')}>
          <textarea
            className="textarea mono"
            rows={5}
            value={mine}
            onChange={(e) => setMine(e.target.value)}
            placeholder={t('Вставь сохранённый текст правил')}
          />
        </Field>
        <div className="row" style={{ gap: 8 }}>
          <Button variant="out" disabled={!mine.trim()} onClick={checkMine}>
            {t('Посчитать хеш')}
          </Button>
          {mineHash && (
            <Badge tone={mineHash === published.toLowerCase() ? 'good' : 'bad'}>
              {mineHash === published.toLowerCase() ? t('совпадает с опубликованным') : t('отличается')}
            </Badge>
          )}
        </div>
        {mineHash && (
          <div className="krow col" style={{ marginTop: 10 }}>
            <span className="k">{t('Хеш твоего экземпляра')}</span>
            <span className="v mono">{mineHash}</span>
          </div>
        )}
        {error && (
          <p className="sub" style={{ color: 'var(--bad)', marginTop: 8 }}>
            {error}
          </p>
        )}
      </Card>

      <Card>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="h2">{t('Полный текст правил')}</span>
          <span className="spacer" />
          <Hash value={published} />
        </div>
        <pre className="rules">{rules || t('Текст правил недоступен.')}</pre>
      </Card>

      <div className="row" style={{ gap: 8, marginTop: 14 }}>
        <Link to={`/c/${c.id}`} className="btn out block">
          {t('К акции')}
        </Link>
        <Link to={`/verify/${c.id}`} className="btn pri block">
          {t('Проверить розыгрыш')}
        </Link>
      </div>

      <Card style={{ marginTop: 14 }}>
        <div className="row" style={{ gap: 9, alignItems: 'flex-start' }}>
          <Icon name="shield" size={16} />
          <span className="sub">
            {t('Хеш — это отпечаток текста. Одинаковый текст всегда даёт одинаковый отпечаток, а любое изменение — совершенно другой. Поэтому организатор не может переписать условия задним числом: старый отпечаток останется в приложении, на странице акции и у всех, кто его сохранил.')}
          </span>
        </div>
      </Card>
    </Shell>
  );
}
